import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './axios-base-query';
import APIS from '../../utils/apiUrl';
import { Bundle, BundleSearchResult, BundleTube, UpdateBundleRequest } from '../../models/bundle';
import { BaseSearchRequest } from '../../models/shared';
import axios from 'axios';
import { SignedUrlResponse } from '../../models/signedUrlResponse';
import { requireAuthToken } from '../../utils/auth/getAuthToken';
import { Device } from '@capacitor/device';

export interface BundleSearchRequest extends BaseSearchRequest<Bundle> {
  organizationId?: string;
  siteName?: string;
  searchTerm?: string;
}
export interface CreateBundleRequest {
  name: string;
  description: string;
  siteName: string;
  heatExchangerModelNumber: string;
  tubeCount?: string;
  tubeLength?: string;
  tubeLengthUnit?: string;
  tubeDiameter?: string;
  tubeDiameterUnit?: string;
  pattern?: 'square' | 'triangle';
  assetNumber?: string;
  pitch?: string;
  pitchUnit?: string;
  permissions?: any[];
  rotated: boolean;
  tubes: BundleTube[];
}

/**
 * A redux-toolkit query api for bundles. The toolkit manages refreshes, caching, cancellation, etc
 * via its generated react hooks (see the exports at the bottom of the file). The hooks return useful
 * helper states like loading, error, or data
 *
 * @see https://redux-toolkit.js.org/rtk-query/overview
 */
export const bundlesApi = createApi({
  reducerPath: 'bundles',
  baseQuery: axiosBaseQuery({ baseUrl: APIS.SENTINEL_API }),
  tagTypes: ['Bundles'],
  endpoints: builder => {
    return {
      searchBundles: builder.query<BundleSearchResult, BundleSearchRequest>({
        query: params => ({ url: '/api/bundles/search', method: 'get', params: buildParams(params) }),
        providesTags: [{ type: 'Bundles', id: 'SEARCH' }],
      }),
      getBundles: builder.query<Bundle, {}>({
        query: id => ({ url: `/api/bundles/${id}/get`, method: 'get' }),
      }),
      createBundle: builder.mutation<Bundle, CreateBundleRequest>({
        query: body => ({ url: '/api/bundles/create', method: 'POST', data: body }),
        invalidatesTags: _ => [{ type: 'Bundles', id: 'SEARCH' }],
        transformResponse: (response: Bundle) => {
          return response;
        },
      }),
      deleteBundle: builder.mutation({
        query: id => ({ url: `/api/bundles/${id}/delete`, method: 'POST' }),
        invalidatesTags: _ => [{ type: 'Bundles', id: 'SEARCH' }],
      }),
      updateBundle: builder.mutation<Bundle, UpdateBundleRequest>({
        query: body => ({ url: `/api/bundles/${body._id}/update`, method: 'POST', data: body }),
        transformResponse: (response: Bundle) => {
          return response;
        },
      }),
    };
  },
});

export const {
  /** a hook to search for bundles, see {@link BundleSearchRequest} for parameters */
  useSearchBundlesQuery,
  useGetBundlesQuery,
  useCreateBundleMutation,
  useDeleteBundleMutation,
  useUpdateBundleMutation,
} = bundlesApi;

const buildParams = (request: BundleSearchRequest): URLSearchParams => {
  const params: URLSearchParams = new URLSearchParams();
  params.append('pageNumber', request.pageNumber.toString());
  params.append('pageSize', request.pageSize.toString());
  if (request.searchTerm) {
    params.append('searchTerm', request.searchTerm);
  }
  if (request.organizationId) {
    params.append('organizationId', request.organizationId);
  }
  if (request.siteName) {
    params.append('siteName', request.siteName);
  }
  if (request.sort) {
    params.append('sortProperty', request.sort.property);
    params.append('sortOrder', request.sort.order);
  }
  return params;
};

export const downloadBundleTemplate = async () => {
  try {
    const token = await requireAuthToken();

    const urlResponse = await getSignedRequest(token);

    const options: any = {
      responseType: 'blob',
      ContentType: 'application/vnd.ms-excel.sheet.macroEnabled.12',
    };
    const docResponse = await axios.get(urlResponse.signedRequest, options);
    const url = window.URL.createObjectURL(new Blob([docResponse.data]));
    const link = document.createElement('a');
    link.href = url;
    const fileName = urlResponse.Key.split('/');
    link.setAttribute('download', fileName[1]);
    document.body.appendChild(link);
    link.click();
  } catch (err) {
    console.error('Unknown failure in bundle helper:', err);
  }
};

const getSignedRequest = async (token: string) => {
  const response = await axios.post<SignedUrlResponse>(
    `${APIS.SENTINEL_API}/api/bundles/getTemplateUrl`,
    {
      languageCode: await getValidLanguageCode(),
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

const getValidLanguageCode = async (): Promise<string> => {
  const languageCode = await Device.getLanguageCode();

  // Default to English version if user locale is not supported
  switch (languageCode.value) {
    case 'nl':
    case 'en':
      return languageCode.value;

    default:
      return 'en';
  }
};
